import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import LeadCapture from "../components/LeadCapture";
import { trackCTA } from "../lib/analytics";
import {
  Building2,
  Shield,
  MapPin,
  UserCircle2,
  Sparkles,
  Briefcase,
  Award,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const SEGMENTS = [
  {
    id: "small-business",
    icon: Building2,
    title: "Small Businesses",
    tagline: "Compete smarter against larger primes.",
    body:
      "Small businesses can't out-spend large primes on capture or proposal labor. GovBidIQ levels the field by surfacing the same FAR/DFARS-aware analysis large firms run internally — for the cost of a single chargeable hour.",
    bullets: [
      "Per-NAICS size standard intelligence",
      "Set-aside fit and disqualification risk",
      "Discriminator surfacing for non-incumbents",
    ],
  },
  {
    id: "sdvosb",
    icon: Shield,
    title: "SDVOSBs",
    tagline: "Identify opportunities where your designation is decisive.",
    body:
      "Service-Disabled Veteran-Owned Small Businesses receive priority consideration on a wide band of federal procurements. GovBidIQ flags SDVOSB set-aside fit, VA-specific advantages (PL 109-461), and DoD pathways where SDVOSB carries weight.",
    bullets: [
      "VA Veterans First validation",
      "DoD SDVOSB set-aside surfacing",
      "Subcontracting plan opportunities",
    ],
  },
  {
    id: "hubzone",
    icon: MapPin,
    title: "HUBZone Firms",
    tagline: "Convert geographic certification into capture advantage.",
    body:
      "HUBZone designation is one of the strongest set-aside levers in the FAR — and one of the most underused. GovBidIQ identifies HUBZone preferences, principal-office location implications, and 35% workforce-residency posture.",
    bullets: [
      "HUBZone set-aside detection",
      "Geographic place-of-performance alignment",
      "Subcontracting credit identification",
    ],
  },
  {
    id: "wosb",
    icon: Award,
    title: "WOSBs / EDWOSBs",
    tagline: "Capitalize on women-owned set-aside opportunities.",
    body:
      "GovBidIQ surfaces WOSB and EDWOSB set-aside indicators across federal procurements — including the growing NAICS list authorized for WOSB sole-source awards.",
    bullets: [
      "WOSB / EDWOSB set-aside detection",
      "NAICS eligibility validation",
      "Sole-source opportunity identification",
    ],
  },
  {
    id: "eight-a",
    icon: Sparkles,
    title: "8(a) Companies",
    tagline: "Maximize the value of your 8(a) tenure.",
    body:
      "8(a) tenure is finite. Every quarter outside the program is a missed competitive advantage. GovBidIQ helps 8(a) firms triage incoming opportunities so capture investment goes to deals with real 8(a) leverage.",
    bullets: [
      "8(a) sole-source eligibility detection",
      "Mentor-Protégé positioning analysis",
      "On-ramp vehicle qualification scoring",
    ],
  },
  {
    id: "first-time",
    icon: UserCircle2,
    title: "First-Time Government Contractors",
    tagline: "Skip a decade of expensive lessons.",
    body:
      "Most first-time federal contractors lose 12-24 months chasing opportunities they were never positioned to win. GovBidIQ encodes capture management experience into a one-click report so new entrants can recognize a real opportunity from a vanity one.",
    bullets: [
      "Plain-English clause and risk explanation",
      "Readiness assessment + barriers to entry",
      "Teaming partner recommendation guidance",
    ],
  },
  {
    id: "existing",
    icon: Briefcase,
    title: "Existing Government Contractors",
    tagline: "Triage your pipeline at the speed of solicitation release.",
    body:
      "Established contractors don't have a capture problem — they have a triage problem. GovBidIQ runs against every solicitation in your pipeline so capture, BD, and proposal leadership can focus on the deals that justify the spend.",
    bullets: [
      "Per-RFP Bid/No-Bid scoring on day one",
      "Pursuit ROI vs. capture investment",
      "Hidden red flag detection on incumbencies",
    ],
  },
];

export default function WhoItsFor() {
  return (
    <div className="grain min-h-screen">
      <Navbar />

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1300px] mx-auto px-6 py-16">
          <div className="tick-label text-sky-400 mb-4">/ Who GovBidIQ Is For</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-[1.05] mb-5 max-w-3xl">
            Built for contractors who need
            <br />
            <span className="text-slate-500">to win — not just to bid.</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-3xl">
            GovBidIQ was designed specifically for federal contractors operating under set-aside
            programs, small business size standards, and capture cycles that simply cannot afford
            wasted proposal labor.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-[1300px] mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-soft)]">
            {SEGMENTS.map((s) => (
              <div
                key={s.id}
                data-testid={`segment-${s.id}`}
                className="bg-[var(--bg-primary)] p-7 hover:bg-[var(--bg-secondary)] transition-colors flex flex-col"
              >
                <s.icon className="w-7 h-7 text-sky-400 mb-5" strokeWidth={1.4} />
                <h2 className="font-display font-bold text-slate-100 text-lg mb-1.5">{s.title}</h2>
                <div className="tick-label mb-4">{s.tagline}</div>
                <p className="text-sm text-slate-400 leading-relaxed mb-5 flex-1">{s.body}</p>
                <ul className="space-y-1.5 mb-5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[13px] text-slate-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-sky-400 flex-shrink-0 mt-1" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--border-soft)] bg-[var(--bg-secondary)]/30">
        <div className="max-w-[1100px] mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="tick-label text-sky-400 mb-3">/ Try Before You Buy</div>
              <h2 className="font-display font-black text-3xl text-slate-100 mb-4 leading-tight">
                See GovBidIQ on one of your real opportunities.
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                Submit an active solicitation you&apos;re considering. We&apos;ll return a
                complimentary preview Opportunity Intelligence Report — scored, with our top three
                Bid/No-Bid concerns.
              </p>
            </div>
            <div className="lg:col-span-7">
              <LeadCapture variant="inline" source="who-its-for" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--border-soft)]">
        <div className="max-w-[1100px] mx-auto px-6 py-16 text-center">
          <h2 className="font-display font-black text-3xl text-slate-100 mb-4">
            Ready to run a full report?
          </h2>
          <Link
            to="/pricing"
            onClick={() => trackCTA("who-its-for-pricing")}
            data-testid="who-its-for-pricing-cta"
            className="inline-flex items-center gap-3 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-xs tracking-[0.2em] uppercase font-bold px-7 py-4 transition-colors"
          >
            View Pricing <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
      </section>
    </div>
  );
}
